import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Translation } from 'react-i18next';
import { isEmpty } from 'lodash';
import withStyles from 'react-jss';
import classNames from 'classnames';

import {
  getEcapState,
  getOnboardingState,
  getAuthState,
  getTenantState,
} from '../../../store/selectors';
import { getTenantDegrees, saveEcap, setEcap } from '../../../store/actions';
import ClassesDragAndDrop from './drag-drop/ClassesDND';
import ClassesDragAndDropMobile from './drag-drop/mobile/ClassesDragAndDropMobile';
import CheckMarkIcon from '../../utility/EcapCheckMark';
import NextButton from '../../utility/NextButton';
import Loading from '../../utility/Loading';
import ECAPModal from '../ECAPModal';
import { handleFireGTM } from '../../utility/routeUtil';

const ecapStyles = {
  color: (props) => ({
    color: props.tenant.primary,
    '&:hover': {
      backgroundColor: 'white',
      textDecoration: 'underline',
    },
    '&:disabled': {
      color: 'grey',
    },
    '&:disabled:hover': {
      textDecoration: 'none',
      cursor: 'not-allowed !important',
    },
  }),
  checkMark: () => ({
    height: '10px',
  }),
};

class ClassesMainContent extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showSpeechBubble: false,
      showModal: true,
      showPlan: true,
    };
  }

  async componentDidMount() {
    this._isMounted = true;
    this.updateWindowDimensions();

    document.addEventListener('mousedown', this.handleClickOutside);
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    document.removeEventListener('mousedown', this.handleClickOutside);

    this._isMounted = false;
  }

  // GETTERS

  get isMobile() {
    return this.state.width < 800;
  }

  // EVENTS

  updateWindowDimensions = () => {
    if (this._isMounted) this.setState({ width: window.innerWidth });
    if (window.innerWidth > 800) {
      this.props.updateMobileSearchMode(false);
    }
  };

  handleClickOutside = () => {
    if (this._isMounted && this.state.showSpeechBubble)
      this.setState({ showSpeechBubble: false });
  };

  handleCloseModal = () => {
    if (this._isMounted && !this.currentDegreeIsNotABachelors) {
      this.setState({ showModal: false });
    }
  };

  handleToggleRecommended = () => {
    if (this._isMounted)
      this.setState((prevState) => ({ showPlan: !prevState.showPlan }));
  };

  showSpeechBubble = () => {
    if (this._isMounted) this.setState({ showSpeechBubble: true });
  };

  saveProgress = async () => {
    await this.props.saveEcap(
      this.props.auth.jwtToken,
      JSON.parse(JSON.stringify(this.props.ecap.plan))
    );

    handleFireGTM(
      this.props.tenant.name,
      'click continue from classes area',
      this.props.location.pathname
    );

    this.props.scrollNext();
  };

  // RENDER

  renderDragAndDrop = () => {
    if (this.isMobile) {
      return (
        <ClassesDragAndDropMobile
          currentDegree={this.props.currentDegree}
          acadPlanReqClasses={this.props.acadPlanReqClasses}
          updateMobileSearchMode={this.props.updateMobileSearchMode}
          showPlan={this.state.showPlan}
          auth={this.props.auth}
          placedClasses={this.props.placedClasses}
          addElectivesRow={this.props.addElectivesRow}
          customClass={this.props.customClass}
          coreSubjectsList={this.props.coreSubjectsList}
          coreSubjectsListEn={this.props.coreSubjectsListEn}
          acadPlanClasses={this.props.acadPlanClasses}
          allClasses={this.props.allClasses}
          progressByDegree={this.props.progressByDegree}
          ecapNotAllowed={this.currentDegreeIsNotABachelors}
        />
      );
    }

    return (
      <ClassesDragAndDrop
        showPlan={this.state.showPlan}
        currentDegree={this.props.currentDegree}
        acadPlanClasses={this.props.acadPlanClasses}
        acadPlanReqClasses={this.props.acadPlanReqClasses}
        placedClasses={this.props.placedClasses}
        addElectivesRow={this.props.addElectivesRow}
        customClass={this.props.customClass}
        coreSubjectsList={this.props.coreSubjectsList}
        coreSubjectsListEn={this.props.coreSubjectsListEn}
        allClasses={this.props.allClasses}
        progressByDegree={this.props.progressByDegree}
        ecapNotAllowed={this.currentDegreeIsNotABachelors}
      />
    );
  };

  get currentDegreeIsNotABachelors() {
    return !this.props.currentDegree.academicProgramTypeEn.includes('Bachelor');
  }

  renderModal() {
    if (this.currentDegreeIsNotABachelors) {
      return (
        <ECAPModal
          tenant={this.props.tenant}
          handleCloseModal={this.handleCloseModal}
          showModal
          isMobile={this.isMobile}
          notAllowedModal
        />
      );
    }
    if (this.state.showModal) {
      return (
        <ECAPModal
          tenant={this.props.tenant}
          handleCloseModal={this.handleCloseModal}
          showModal={this.state.showModal}
          isMobile={this.isMobile}
          notAllowedModal={false}
        />
      );
    }
  }

  renderContent(t) {
    if (isEmpty(this.props.acadPlanReqClasses)) {
      return <Loading height="100vh" />;
    }

    return (
      <>
        {this.renderDragAndDrop()}
        <div className="continue-button-disclaimer-text">
          <NextButton
            ariaLabel={t('Save your progress and continue')}
            overRideStyle="continue-button"
            overRideButtonText="Continue"
            onClick={this.saveProgress}
            color={this.props.tenant.primary}
            isActive
          />
          <p className="ecap-disclaimer-text">
            {t(
              '{{toolName}} is to be used as a tool to guide in recommending courses to take in high school. Please speak with an ASU representative, or representative from the institution you are interested in, to confirm you meet specific admissions requirements for your program of interest.',
              { toolName: 'ECAP' }
            )}
          </p>
        </div>
      </>
    );
  }

  render() {
    return (
      <Translation>
        {(t) => (
          <div id="classes-main-content">
            <div className="class-toggle-speech-bubble-container">
              <button
                className={classNames(
                  'ecap-check-mark-req-text',
                  this.props.classes.color
                )}
                disabled={this.currentDegreeIsNotABachelors}
                onClick={this.props.showDegreeRequirements}
                type="button"
              >
                <span className="ecap-check-mark">
                  <CheckMarkIcon
                    color={
                      this.currentDegreeIsNotABachelors
                        ? 'grey'
                        : this.props.tenant.primary
                    }
                  />
                </span>
                {t('View Your Degree Requirements Summary')}
              </button>
            </div>
            <div
              className="classes-middle-section"
              aria-labelledby="instructions-text"
            >
              {this.renderModal()}
              {this.renderContent(t)}
            </div>
          </div>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const onboarding = getOnboardingState(state);
  const ecap = getEcapState(state);
  const auth = getAuthState(state);
  const tenant = getTenantState(state);

  return { onboarding, auth, tenant, ecap };
};

export default withRouter(
  connect(mapStateToProps, {
    getTenantDegrees,
    saveEcap,
    setEcap,
  })(withStyles(ecapStyles)(ClassesMainContent))
);
